import { Component, type ErrorInfo, type ReactNode } from 'react'
import { createLogger } from '@ondit/logger'
import { Chip } from '@ondit/ui'
import { ErrorShell } from '@/screens/workspace/components'
import { useT } from '~/core/i18n'

const logger = createLogger('ErrorBoundary')

/**
 * Shared Error UI Component
 */
interface ErrorUIProps {
  title?: string
  message?: string
  onReset?: () => void
}

export function ErrorUI({ title, message, onReset }: ErrorUIProps) {
  const t = useT()
  return (
    <ErrorShell
      title={title ?? t('Something went wrong')}
      description={
        message ?? t('An unexpected error occurred. Please try again or refresh the page.')
      }
    >
      <Chip variant='primary' onClick={onReset ?? (() => window.location.reload())}>
        {t('Try again')}
      </Chip>
    </ErrorShell>
  )
}

/**
 * React Error Boundary Component
 * Catches React rendering errors and displays ErrorUI fallback
 */
interface ErrorBoundaryProps {
  children: ReactNode
  fallback?: ReactNode
}

interface ErrorBoundaryState {
  hasError: boolean
  error?: Error
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  public state: ErrorBoundaryState = {
    hasError: false,
  }

  public static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error }
  }

  /**
   * Reports what was caught. This boundary latches for the life of the document
   * and its fallback names nothing, so without this the only trace of a canvas
   * crash is React's own console output on whichever machine happened to hit it
   * — leaving an intermittent failure with no evidence to diagnose from.
   * `error.name` is carried separately from the message because it is what
   * separates the failure classes from each other.
   */
  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const componentStack = errorInfo.componentStack ?? undefined
    logger.error('Workflow canvas crashed', {
      name: error.name,
      message: error.message,
      stack: error.stack,
      componentStack,
    })
  }

  public render() {
    if (this.state.hasError) {
      return this.props.fallback || <ErrorUI />
    }

    return this.props.children
  }
}
